import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, Minus, Trophy, Ticket } from 'lucide-react';

const getBadgeVariant = (status) => {
  if (status === 'Ending Soon') return 'destructive';
  if (status === 'New') return 'default';
  return 'outline';
};

const getEvColor = (evPercent) => {
  if (evPercent >= 60) return 'text-success';
  if (evPercent >= 40) return 'text-chart-3';
  if (evPercent >= 20) return 'text-warning';
  return 'text-danger';
};

const getOddsIcon = (trend) => {
  if (trend === 'IMPROVING') return <TrendingUp className="w-4 h-4 text-success" />;
  if (trend === 'DECLINING') return <TrendingDown className="w-4 h-4 text-danger" />;
  return <Minus className="w-4 h-4 text-muted-foreground" />;
};

export const RecommendationsTable = ({ data, onSelect, isLoading }) => {
  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="recommendations-loading">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-muted/40 animate-pulse rounded" />
        ))}
      </div>
    );
  }
  
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="recommendations-empty">
        <p className="text-lg mb-2">No games found</p>
        <p className="text-sm">Try adjusting your filters or refresh the data</p>
      </div>
    );
  }
  
  return (
    <TooltipProvider>
      <div className="border border-border rounded-lg overflow-hidden">
        <Table data-testid="recommendations-table">
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead className="w-12">Rank</TableHead>
              <TableHead className="w-16"></TableHead>
              <TableHead>Game</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">EV %</TableHead>
              <TableHead className="text-center">Jackpot</TableHead>
              <TableHead className="text-right">Top Prize</TableHead>
              <TableHead className="text-right">Tickets Left</TableHead>
              <TableHead className="text-right">Prizes Left</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Trend</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => (
              <TableRow 
                key={row.id} 
                className="cursor-pointer hover:bg-muted/60 transition-colors"
                onClick={() => onSelect(row)}
                data-testid="recommendations-row"
              >
                <TableCell className="font-semibold text-accent">
                  #{row.rank}
                </TableCell>
                <TableCell className="p-1">
                  <div className="w-12 h-12 rounded-md overflow-hidden bg-muted border border-border">
                    <img 
                      src={row.image_url || `https://walottery.com/scratch/assets/imgs/tickets/grid/${row.game_id}.jpg`}
                      alt={row.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  <div className="max-w-xs">
                    <div className="truncate">{row.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">#{row.game_id}</div>
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono">${row.price.toFixed(0)}</TableCell>
                <TableCell className={`text-right font-mono font-semibold ${getEvColor(row.ev_percent)}`}>
                  {row.ev_percent.toFixed(1)}%
                </TableCell>
                <TableCell className="text-center">
                  {row.jackpot_available ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <Trophy className="w-4 h-4 text-warning inline" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Top prize still available!</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {row.top_prize_remaining > 0 ? (
                    <div className="font-mono text-sm">
                      <div>{row.top_prize_remaining} left</div>
                      <div className="text-xs text-muted-foreground">${(row.top_prize_amount / 1000).toFixed(0)}K</div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {row.estimated_tickets_remaining ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="flex items-center justify-end gap-1">
                          <Ticket className="w-3 h-3" />
                          {(row.estimated_tickets_remaining / 1000).toFixed(0)}K
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">{row.estimated_tickets_remaining.toLocaleString()} estimated tickets</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <Progress value={row.prizes_remaining} className="w-16 h-2" />
                    <span className="text-sm font-mono">{row.prizes_remaining.toFixed(0)}%</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={getBadgeVariant(row.status)}>
                    {row.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Tooltip>
                    <TooltipTrigger>
                      {getOddsIcon(row.odds_trend)}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Odds trend: {row.odds_trend || 'Unknown'}</p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
};
